import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useGetMyCoursesQuery, useGetMyPendingOrdersQuery } from '../../api/apiSlice';
import {
  Loader2,
  AlertTriangle,
  BookOpen,
  PlayCircle,
  BarChart3,
  CheckCircle2,
  Search,
  Clock3,
  Video as VideoIcon,
  FileText,
  HelpCircle,
  ArrowRight,
  Award,
  Hourglass,
  Phone,
  Send,
  MessageCircle,
  CalendarX2,
  AlarmClock,
} from 'lucide-react';
import { COURSE_COVER_FALLBACK_SM, setFallback } from '../../lib/placeholders';
import Pagination from '../../components/common/Pagination';

const PAGE_SIZE = 9;

// Лейблы фильтров/сортировки разворачиваются через t() внутри компонента —
// массивы остаются как источник id'шек, чтобы не лазить по JSX.
const FILTER_IDS = ['all', 'inprogress', 'completed', 'notstarted'];
const SORT_IDS = ['recent', 'progress', 'title'];

const formatDate = (iso, locale) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString(locale, {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return null;
  }
};

// daysLeft до expiresAt. Возвращает null если бессрочно (старые legacy enrollments).
const daysUntilExpiry = (iso) => {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  return Math.ceil(ms / (1000 * 60 * 60 * 24));
};

const MyCourses = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.resolvedLanguage === 'kk' ? 'kk-KZ' : 'ru-RU';
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('all');
  const [sort, setSort] = useState('recent');
  const [page, setPage] = useState(1);

  const { data: enrollments = [], isLoading, error } = useGetMyCoursesQuery();
  // Заявки в статусе pending_contact — студент оставил контакт, ждёт связи
  // от админа. Показываем отдельной секцией над основной сеткой "куплено",
  // иначе студент в недоумении: "я нажал купить, а где курс?".
  const { data: pendingData } = useGetMyPendingOrdersQuery();
  const pendingOrders = (pendingData?.orders || []).filter(
    (o) => o.status === 'pending_contact'
  );

  useEffect(() => {
    setPage(1);
  }, [searchTerm, filter, sort]);

  const filtered = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();

    let list = enrollments.filter((en) => {
      const titleHit = !q || en.course?.title?.toLowerCase().includes(q);
      if (!titleHit) return false;

      const p = en.progress || 0;
      // completedAt — иммутабельная дата завершения с бэка. Если стоит —
      // курс пройден, даже если progress сейчас <100 (юзер мог снять
      // галочку с урока после прохождения).
      const isDone = !!en.completedAt || p >= 100;
      if (filter === 'inprogress') return !isDone && p > 0;
      if (filter === 'completed') return isDone;
      if (filter === 'notstarted') return p === 0;
      return true;
    });

    list = [...list].sort((a, b) => {
      if (sort === 'progress') return (b.progress || 0) - (a.progress || 0);
      if (sort === 'title')
        return (a.course?.title || '').localeCompare(b.course?.title || '', 'ru');
      // recent
      return new Date(b.lastAccess || b.purchaseDate || 0) - new Date(a.lastAccess || a.purchaseDate || 0);
    });

    return list;
  }, [enrollments, searchTerm, filter, sort]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const completedTotal = enrollments.filter((e) => !!e.completedAt || (e.progress || 0) >= 100).length;

  if (isLoading) {
    // Skeleton 6 карточек — perceived-perf лучше чем центральный спиннер.
    // Юзер видит макет страницы сразу, не «пустоту → внезапно контент».
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-950 dark:to-gray-900 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8">
            <div className="h-9 w-40 rounded bg-gray-200 dark:bg-gray-800 animate-pulse mb-2" />
            <div className="h-4 w-56 rounded bg-gray-200 dark:bg-gray-800 animate-pulse" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden"
              >
                <div className="h-44 bg-gray-200 dark:bg-gray-700 animate-pulse" />
                <div className="p-5 space-y-3">
                  <div className="h-5 w-3/4 rounded bg-gray-200 dark:bg-gray-700 animate-pulse" />
                  <div className="h-4 w-1/2 rounded bg-gray-200 dark:bg-gray-700 animate-pulse" />
                  <div className="h-2 w-full rounded-full bg-gray-200 dark:bg-gray-700 animate-pulse" />
                  <div className="flex gap-2">
                    <div className="h-4 w-16 rounded bg-gray-200 dark:bg-gray-700 animate-pulse" />
                    <div className="h-4 w-20 rounded bg-gray-200 dark:bg-gray-700 animate-pulse" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-950 dark:to-gray-900 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-lg rounded-3xl border border-red-100 dark:border-red-900/40 bg-white dark:bg-gray-900 shadow-xl p-6 sm:p-8 text-center">
          <div className="flex justify-center mb-5">
            <div className="rounded-full bg-red-100 dark:bg-red-500/10 p-4">
              <AlertTriangle className="w-10 h-10 text-red-500" />
            </div>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">{t('studentMyCourses.loadErrorTitle')}</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            {error?.data?.message || t('studentMyCourses.loadError')}
          </p>
          <button
            onClick={() => window.location.reload()}
            className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-indigo-600 text-white font-medium hover:bg-indigo-700 transition-all"
          >
            {t('studentMyCourses.retry')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-950 dark:to-gray-900 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-1">
              {t('studentMyCourses.title')}
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              {t('studentMyCourses.summary', { purchased: enrollments.length, completed: completedTotal })}
            </p>
          </div>
        </div>

        {/* Ожидают подтверждения оплаты — заявки в статусе pending_contact.
            Курс ещё не открыт, но студент знает что заявка ушла и админ скоро
            свяжется. Без этого UX дыра — кликнул "купить", а на My Courses пусто. */}
        {pendingOrders.length > 0 && (
          <div className="mb-6 rounded-2xl border border-amber-200 dark:border-amber-900/40 bg-amber-50/70 dark:bg-amber-950/20 overflow-hidden">
            <div className="px-5 py-3 border-b border-amber-200/70 dark:border-amber-900/40 flex items-center gap-2">
              <Hourglass className="h-4 w-4 text-amber-600 dark:text-amber-400" />
              <h2 className="text-sm font-semibold text-amber-900 dark:text-amber-200">
                {t('studentMyCourses.pendingHeader', { count: pendingOrders.length })}
              </h2>
            </div>
            <div className="divide-y divide-amber-200/60 dark:divide-amber-900/30">
              {pendingOrders.map((order) => (
                <div key={order.id} className="px-5 py-3 flex items-center gap-3">
                  <div className="h-12 w-16 sm:h-14 sm:w-20 rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-800 shrink-0">
                    {order.course?.coverImageUrl && (
                      <img
                        src={order.course.coverImageUrl}
                        alt={order.course.title}
                        className="h-full w-full object-cover"
                      />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                      {order.course?.title || t('studentMyCourses.pendingNoteCourse')}
                    </div>
                    <div className="text-xs text-amber-700 dark:text-amber-300 mt-0.5 flex items-center gap-1">
                      <Phone className="h-3 w-3" />
                      {t('studentMyCourses.pendingNoteWait')}
                    </div>
                  </div>
                  <div className="text-sm font-semibold text-gray-900 dark:text-white tabular-nums shrink-0">
                    {order.amount?.toLocaleString('ru-RU')} ₸
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Toolbar */}
        {enrollments.length > 0 && (
          <div className="mb-6 grid gap-3 sm:grid-cols-[1fr_auto_auto]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder={t('studentMyCourses.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="px-3 py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              {FILTER_IDS.map((id) => (
                <option key={id} value={id}>{t(`studentMyCourses.filters.${id}`)}</option>
              ))}
            </select>
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="px-3 py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              {SORT_IDS.map((id) => (
                <option key={id} value={id}>{t(`studentMyCourses.sorts.${id}`)}</option>
              ))}
            </select>
          </div>
        )}

        {/* Grid */}
        {paged.length > 0 ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {paged.map((enrollment) => {
                const course = enrollment.course || {};
                const progress = enrollment.progress || 0;
                // Иммутабельный completed: даже если progress<100 (юзер снял
                // галочку), но completedAt есть на бэке — курс был пройден.
                const completed = !!enrollment.completedAt || progress >= 100;
                const lastAt = formatDate(enrollment.lastAccess || enrollment.purchaseDate, dateLocale);
                // Срок доступа — backend выставляет 1 год от покупки. Legacy
                // enrollments (expiresAt=null) трактуем как бессрочные.
                const isExpired = !!enrollment.expired;
                const daysLeft = daysUntilExpiry(enrollment.expiresAt);
                const isExpiringSoon = !isExpired && daysLeft !== null && daysLeft <= 30 && daysLeft > 0;
                const expiryStr = formatDate(enrollment.expiresAt, dateLocale);

                return (
                  <article
                    key={enrollment._id}
                    onClick={() => navigate(`/learn/${course._id}`)}
                    className={`group flex flex-col rounded-2xl border bg-white dark:bg-gray-800 shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden cursor-pointer hover:-translate-y-1 ${
                      isExpired
                        ? 'border-red-200 dark:border-red-900/40 opacity-80'
                        : 'border-gray-200 dark:border-gray-700'
                    }`}
                  >
                    <div className="relative w-full aspect-video bg-gradient-to-br from-indigo-600 to-purple-600 overflow-hidden">
                      {/* Backdrop + object-contain паттерн: обложка видна
                          ЦЕЛИКОМ, поля заполнены размытой копией. Все карточки
                          одной высоты благодаря aspect-video на контейнере. */}
                      {course.coverImageUrl && (
                        <img
                          src={course.coverImageUrl}
                          alt=""
                          aria-hidden="true"
                          loading="lazy"
                          className="absolute inset-0 h-full w-full object-cover scale-110 blur-2xl opacity-50 dark:opacity-30"
                        />
                      )}
                      <img
                        src={course.coverImageUrl || COURSE_COVER_FALLBACK_SM}
                        alt={course.title}
                        loading="lazy"
                        onError={setFallback(COURSE_COVER_FALLBACK_SM)}
                        className="absolute inset-0 h-full w-full object-contain group-hover:scale-105 transition-transform duration-300"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <PlayCircle className="w-14 h-14 text-white drop-shadow-lg" />
                      </div>
                      {completed && !isExpired && (
                        <div className="absolute right-3 top-3 inline-flex items-center gap-1 rounded-full bg-emerald-500 px-2.5 py-1 text-xs font-semibold text-white shadow-md">
                          <CheckCircle2 className="w-3.5 h-3.5" />
                          {t('studentMyCourses.card.completed')}
                        </div>
                      )}
                      {isExpired && (
                        <div className="absolute right-3 top-3 inline-flex items-center gap-1 rounded-full bg-red-600 px-2.5 py-1 text-xs font-semibold text-white shadow-md">
                          <CalendarX2 className="w-3.5 h-3.5" />
                          {t('studentMyCourses.card.expired')}
                        </div>
                      )}
                      {isExpiringSoon && (
                        <div className="absolute right-3 top-3 inline-flex items-center gap-1 rounded-full bg-amber-500 px-2.5 py-1 text-xs font-semibold text-white shadow-md">
                          <AlarmClock className="w-3.5 h-3.5" />
                          {t('studentMyCourses.card.expiresIn', { count: daysLeft })}
                        </div>
                      )}
                      <div className="absolute left-3 bottom-3 inline-flex items-center gap-1 rounded-full bg-white/95 dark:bg-gray-900/90 px-2.5 py-1 text-xs font-semibold text-gray-700 dark:text-gray-200 shadow-sm backdrop-blur tabular-nums">
                        {Math.round(progress)}%
                      </div>
                    </div>

                    <div className="flex flex-1 flex-col p-5">
                      <h3 className="line-clamp-2 text-lg font-semibold text-gray-900 dark:text-white mb-2 break-words">
                        {course.title}
                      </h3>

                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-3 truncate break-words">
                        {course.instructor?.name || t('studentMyCourses.card.instructorFallback')}
                      </p>

                      {/* Type breakdown. Бэк отдаёт course.typeCounts (новый
                          контракт). Если поле отсутствует (legacy кэш), молча
                          скипаем — не падаем. */}
                      {course.typeCounts && (
                        <div className="flex flex-wrap gap-1.5 mb-3">
                          {course.typeCounts.video > 0 && (
                            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 px-2 py-0.5 text-[11px] font-medium">
                              <VideoIcon className="w-3 h-3" />
                              {course.typeCounts.video}
                            </span>
                          )}
                          {course.typeCounts.theory > 0 && (
                            <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 dark:bg-blue-500/10 text-blue-700 dark:text-blue-300 px-2 py-0.5 text-[11px] font-medium">
                              <FileText className="w-3 h-3" />
                              {course.typeCounts.theory}
                            </span>
                          )}
                          {course.typeCounts.quiz > 0 && (
                            <span className="inline-flex items-center gap-1 rounded-full bg-purple-50 dark:bg-purple-500/10 text-purple-700 dark:text-purple-300 px-2 py-0.5 text-[11px] font-medium">
                              <HelpCircle className="w-3 h-3" />
                              {course.typeCounts.quiz}
                            </span>
                          )}
                        </div>
                      )}

                      <div className="mb-4 mt-auto">
                        <div className="flex items-center justify-between text-[11px] text-gray-500 dark:text-gray-400 mb-1 tabular-nums">
                          <span>
                            {/* Контракт фронта: completedCount + totalLessons
                                идут с бэка отдельными полями. Раньше показывали
                                completedLessons.length без total → выглядело
                                как «5 уроков» когда их всего 30 в курсе. */}
                            {t('studentMyCourses.card.lessonsCount', {
                              done: enrollment.completedCount ?? enrollment.completedLessons?.length ?? 0,
                              total: enrollment.totalLessons ?? course.totalLessons ?? 0,
                            })}
                          </span>
                          <span>{Math.round(progress)}%</span>
                        </div>
                        <div className="w-full h-2 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
                          <div
                            className={`h-full transition-all duration-500 ${
                              completed
                                ? 'bg-emerald-500'
                                : 'bg-gradient-to-r from-indigo-600 to-purple-600'
                            }`}
                            style={{ width: `${progress}%` }}
                          />
                        </div>
                      </div>

                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
                          <span className="inline-flex items-center gap-1">
                            <BookOpen className="w-3.5 h-3.5" />
                            {t('studentMyCourses.card.sectionsCount', {
                              count: course.sectionsCount ?? course.sections?.length ?? 0,
                            })}
                          </span>
                          {lastAt && (
                            <span className="inline-flex items-center gap-1">
                              <Clock3 className="w-3.5 h-3.5" /> {lastAt}
                            </span>
                          )}
                          {expiryStr && (
                            <span
                              className={`inline-flex items-center gap-1 ${
                                isExpired
                                  ? 'text-red-600 dark:text-red-400 font-semibold'
                                  : isExpiringSoon
                                  ? 'text-amber-600 dark:text-amber-400'
                                  : ''
                              }`}
                              title={t('studentMyCourses.card.accessUntil')}
                            >
                              {isExpired ? <CalendarX2 className="w-3.5 h-3.5" /> : <AlarmClock className="w-3.5 h-3.5" />}
                              {isExpired
                                ? t('studentMyCourses.card.expiredOn', { date: expiryStr })
                                : t('studentMyCourses.card.accessUntilDate', { date: expiryStr })}
                            </span>
                          )}
                        </div>
                        <span className="inline-flex items-center gap-1 text-xs font-semibold text-indigo-600 dark:text-indigo-400 group-hover:gap-2 transition-all">
                          {progress === 0
                            ? t('studentMyCourses.card.ctaStart')
                            : completed
                            ? t('studentMyCourses.card.ctaRepeat')
                            : t('studentMyCourses.card.ctaContinue')}
                          <ArrowRight className="w-3.5 h-3.5" />
                        </span>
                      </div>

                      {/* Сообщество — Telegram/WhatsApp ссылки доступны
                          СРАЗУ из "Мои курсы", не нужно открывать курс.
                          stopPropagation чтобы клик не открыл /learn. */}
                      {(course.telegramGroupUrl || course.whatsappGroupUrl) && (
                        <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700/60 flex items-center gap-2">
                          <span className="text-[11px] text-gray-500 dark:text-gray-400 font-medium">
                            {t('studentMyCourses.card.chatLabel')}
                          </span>
                          {course.telegramGroupUrl && (
                            <a
                              href={course.telegramGroupUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-sky-50 dark:bg-sky-950/30 text-sky-700 dark:text-sky-300 text-[11px] font-medium hover:bg-sky-100 dark:hover:bg-sky-950/50 transition"
                            >
                              <Send className="w-3 h-3" /> Telegram
                            </a>
                          )}
                          {course.whatsappGroupUrl && (
                            <a
                              href={course.whatsappGroupUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-300 text-[11px] font-medium hover:bg-emerald-100 dark:hover:bg-emerald-950/50 transition"
                            >
                              <MessageCircle className="w-3 h-3" /> WhatsApp
                            </a>
                          )}
                        </div>
                      )}

                      {/* Для завершённых курсов — отдельный CTA на сертификат.
                          stopPropagation чтобы клик на «Сертификат» не открыл
                          курс (cursor-pointer на всей карточке). */}
                      {completed && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate('/my-certificates');
                          }}
                          className="mt-3 inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white px-4 py-2 text-sm font-semibold shadow-sm w-full transition"
                        >
                          <Award className="w-4 h-4" />
                          {t('studentMyCourses.card.myCertificate')}
                        </button>
                      )}
                    </div>
                  </article>
                );
              })}
            </div>

            <div className="mt-10">
              <Pagination
                page={page}
                pageCount={pageCount}
                onChange={(p) => {
                  setPage(p);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
              />
            </div>
          </>
        ) : enrollments.length === 0 ? (
          // Если есть pending заявки — не показываем "вы не приобрели",
          // блок ожидания выше уже всё объяснил.
          pendingOrders.length > 0 ? null : (
            <div className="rounded-2xl border border-dashed border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/50 p-12 text-center">
              <BookOpen className="w-16 h-16 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                {t('studentMyCourses.emptyTitle')}
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                {t('studentMyCourses.emptyHint')}
              </p>
              <button
                onClick={() => navigate('/courses')}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-indigo-600 text-white font-medium hover:bg-indigo-700 transition-all"
              >
                {t('studentMyCourses.findCourse')}
              </button>
            </div>
          )
        ) : (
          <div className="rounded-2xl border border-dashed border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/50 p-12 text-center">
            <Search className="w-12 h-12 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              {t('studentMyCourses.noResultsTitle')}
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              {t('studentMyCourses.noResultsHint')}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default MyCourses;
